import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
  Method,
  ResponseType
} from "axios";
import { stringify } from "qs";
import { createUrl } from "./createUrl";

type ParamValueType = string | string[] | number | boolean | undefined | null;

/**
 * Эндпоинты auth-флоу, которые не нужно «перехватывать» на 401 ради refresh —
 * иначе получим бесконечную петлю.
 */
const AUTH_REFRESH_PATH = "/auth/refresh";
const AUTH_LOGIN_PATHS = ["/auth/email/verify", "/auth/telegram/exchange"];

type ExtendedRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
  _skipAuthRefresh?: boolean;
};

/**
 * Колбэк, который AuthProvider регистрирует, чтобы при окончательном 401
 * (refresh подтвердил отсутствие сессии) переключить состояние на guest.
 */
let onUnauthorizedHandler: (() => void) | null = null;

export const setOnUnauthorized = (handler: (() => void) | null): void => {
  onUnauthorizedHandler = handler;
};

export const apiClient: AxiosInstance = axios.create({
  withCredentials: true
});

// Сериализуем параллельные refresh-попытки: пока идёт один — остальные ждут его.
type RefreshResult =
  | { status: "ok" }
  | { status: "unauthorized" }
  | { status: "transient"; error: unknown };

let refreshInFlight: Promise<RefreshResult> | null = null;

const tryRefresh = async (baseURL: string): Promise<RefreshResult> => {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async (): Promise<RefreshResult> => {
    try {
      await apiClient.post(
        AUTH_REFRESH_PATH,
        {},
        {
          baseURL,
          // отметка, чтобы interceptor не пытался рефрешить сам refresh-запрос
          _skipAuthRefresh: true
        } as ExtendedRequestConfig
      );
      return { status: "ok" };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        // Нет ответа / 5xx — не считаем сессию мёртвой, иначе ложные логины при сети.
        if (!error.response || (status != null && status >= 500)) {
          return { status: "transient", error };
        }
        if (status === 401 || status === 403) {
          return { status: "unauthorized" };
        }
      }
      return { status: "unauthorized" };
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as ExtendedRequestConfig | undefined;
    const status = error.response?.status;
    const requestUrl = originalRequest?.url ?? "";

    const isAuthRequest =
      originalRequest?._skipAuthRefresh ||
      requestUrl.endsWith(AUTH_REFRESH_PATH) ||
      AUTH_LOGIN_PATHS.some((p) => requestUrl.endsWith(p));

    if (status === 401 && originalRequest && !originalRequest._retry && !isAuthRequest) {
      originalRequest._retry = true;
      const refreshed = await tryRefresh(originalRequest.baseURL ?? "");
      if (refreshed.status === "ok") {
        return apiClient(originalRequest);
      }
      if (refreshed.status === "transient") {
        // Сеть моргнула на refresh — не разлогиниваем, отдаём сетевую ошибку.
        return Promise.reject(refreshed.error);
      }
      // Refresh подтвердил: сессии нет.
      if (onUnauthorizedHandler) {
        onUnauthorizedHandler();
      }
    }

    return Promise.reject(error);
  }
);

export async function request<T, D = unknown>(
  endpoint: string,
  method: Method = "GET",
  params: Record<string, ParamValueType> = {},
  data?: D,
  additionalHeaders: Record<string, string> = {},
  responseType?: ResponseType,
  signal?: AbortSignal
): Promise<T> {
  const baseUrl = await createUrl();
  const hasBody = ["POST", "PATCH", "PUT"].includes(method);
  const config: AxiosRequestConfig = {
    url: endpoint,
    baseURL: baseUrl,
    method,
    headers: {
      Accept: "application/json",
      ...(hasBody ? { "Content-Type": "application/json" } : {}),
      ...additionalHeaders
    },
    params,
    ...(hasBody ? { data } : {}),
    paramsSerializer: (p): string => stringify(p, { arrayFormat: "comma" }),
    signal
  };

  if (responseType) {
    config.responseType = responseType;
  }

  try {
    const response = await apiClient.request<T>(config);
    return response.data;
  } catch (error) {
    if (axios.isCancel(error)) {
      throw new Error("Request was canceled");
    }
    throw error;
  }
}

export function get<T>(url: string, params?: Record<string, ParamValueType>): Promise<T> {
  return request(url, "GET", params);
}

export function post<T, D = unknown>(
  url: string,
  params?: Record<string, ParamValueType>,
  data?: D
): Promise<T> {
  return request(url, "POST", params, data);
}

export function put<T, D>(
  url: string,
  params?: Record<string, ParamValueType>,
  data?: D
): Promise<T> {
  return request(url, "PUT", params, data);
}

export function patch<T, D = unknown>(
  url: string,
  params?: Record<string, ParamValueType>,
  data?: D
): Promise<T> {
  return request(url, "PATCH", params, data);
}

export function del(url: string, params?: Record<string, ParamValueType>): Promise<any> {
  return request(url, "DELETE", params);
}

export async function requestFormData<T>(
  endpoint: string,
  method: "POST" | "PUT" | "PATCH" = "POST",
  params: Record<string, ParamValueType> = {},
  formData: FormData
): Promise<T> {
  return request(endpoint, method, params, formData, {
    "Content-Type": "multipart/form-data"
  });
}
